import React, { useState, useEffect } from 'react';
import { Box, Card, CardContent, Typography, Button, Grid, Chip } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorIcon from '@mui/icons-material/Error';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useApp } from '../context/AppContext';
import { useLanguage } from '../context/LanguageContext';

export default function KDSView() {
    const { orders, tables, advanceOrderStatus, menuItems, currentUser } = useApp();
    const { t, currentLanguage } = useLanguage();
    const [kitchenFilter, setKitchenFilter] = useState('All');
    
    // Tick to update timers every 30 seconds
    const [tick, setTick] = useState(0);
    useEffect(() => {
        const interval = setInterval(() => setTick(t => t + 1), 30000);
        return () => clearInterval(interval);
    }, []);

    const activeBranchId = currentUser?.branch_id || localStorage.getItem('dinexa_active_branch_id') || 1;

    // Helper to identify if item belongs to selected cook station
    const isItemInKitchen = (itemId, filter) => {
        const itemObj = menuItems.find(m => m.id === itemId);
        if (!itemObj) return false;
        if (filter === 'All') return true;

        const isTandoor = itemObj.category === 'breads' || 
                          itemObj.name.toLowerCase().includes('tikka') || 
                          itemObj.name.toLowerCase().includes('tandoori');

        if (filter === 'tandoor') return isTandoor;
        if (isTandoor) return false; // Exclude tandoor items from veg/non-veg stations

        if (filter === 'veg') return itemObj.veg === true;
        if (filter === 'nonveg') return itemObj.veg === false;

        return true;
    };

    // Filter active orders in kitchen strictly for active branch
    const activeKitchenOrders = (orders || []).filter(o => {
        const isBranchMatch = !o.branch_id || String(o.branch_id) === String(activeBranchId);
        const isActiveStatus = (o.status === 'preparing' || o.status === 'ready' || o.status === 'cooking' || o.status === 'pending');
        return isBranchMatch && isActiveStatus;
    });

    // Cross-off local item tracking state (items crossed off inside a KOT)
    const [crossedItems, setCrossedItems] = useState({});
    const toggleCrossItem = (orderId, itemId) => {
        const key = `${orderId}-${itemId}`;
        setCrossedItems(prev => ({ ...prev, [key]: !prev[key] }));
    };

    let renderedCount = 0;

    return (
        <Box sx={{ p: 4, bgcolor: '#f3f4f6', minHeight: 'calc(100vh - 70px)', display: 'flex', flexDirection: 'column' }}>
            {/* Filter Bar */}
            <Box sx={{ display: 'flex', gap: 1.5, mb: 4, overflowX: 'auto' }}>
                {[
                    { id: 'All', label: t('kds-all-stations') },
                    { id: 'veg', label: t('kds-veg-station') },
                    { id: 'nonveg', label: t('kds-nonveg-station') },
                    { id: 'tandoor', label: t('kds-tandoor-station') }
                ].map(station => (
                    <Chip
                        key={station.id}
                        label={station.label}
                        onClick={() => setKitchenFilter(station.id)}
                        variant={kitchenFilter === station.id ? 'filled' : 'outlined'}
                        sx={{
                            fontWeight: 800,
                            fontSize: '0.85rem',
                            height: 38,
                            px: 1,
                            bgcolor: kitchenFilter === station.id ? '#111827' : '#ffffff',
                            color: kitchenFilter === station.id ? '#ffffff' : '#374151',
                            borderColor: '#d1d5db',
                            '&:hover': {
                                bgcolor: kitchenFilter === station.id ? '#1f2937' : '#f9fafb'
                            }
                        }}
                    />
                ))}
            </Box>

            {/* Tickets Grid */}
            <Box sx={{ flexGrow: 1 }}>
                <Grid container spacing={3}>
                    {activeKitchenOrders.map(order => {
                        // Filter items that belong to the active kitchen station
                        const filteredItems = order.items.filter(item => isItemInKitchen(item.id, kitchenFilter));
                        if (filteredItems.length === 0) return null;

                        renderedCount++;
                        const table = tables.find(t => t.id === order.tableId);
                        const tableName = table ? `Table ${table.number}` : order.type;

                        // Calculate elapsed time
                        const minsElapsed = Math.floor((Date.now() - new Date(order.timestamp).getTime()) / 60000);
                        
                        // Determine urgency styles
                        let timerColor = '#10b981';
                        let timerIcon = <AccessTimeIcon sx={{ fontSize: 16 }} />;
                        let cardBorder = '1px solid #e5e7eb';
                        let headerBg = '#f9fafb';

                        if (minsElapsed >= 5 && minsElapsed <= 10) {
                            timerColor = '#f59e0b';
                            timerIcon = <WarningAmberIcon sx={{ fontSize: 16 }} />;
                            cardBorder = '2px solid #f59e0b';
                            headerBg = '#fffbeb';
                        } else if (minsElapsed > 10) {
                            timerColor = '#ef4444';
                            timerIcon = <ErrorIcon sx={{ fontSize: 16 }} />;
                            cardBorder = '2px solid #ef4444';
                            headerBg = '#fef2f2';
                        }

                        const buttonText = order.status === 'preparing' ? t('kds-ready-btn') : t('kds-served-btn');

                        const hasNonVeg = order.items.some(item => {
                            const menuItem = menuItems.find(m => m.id === item.id);
                            return menuItem && menuItem.veg === false;
                        });

                        return (
                            <Grid item xs={12} sm={6} md={4} lg={3} key={order.id}>
                                <Card sx={{ 
                                    borderRadius: 3, 
                                    border: cardBorder,
                                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    height: '100%',
                                    minHeight: 280
                                }}>
                                    {/* Card Header */}
                                    <Box sx={{ 
                                        p: 2, 
                                        bgcolor: headerBg, 
                                        borderBottom: '1px solid #e5e7eb',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1
                                    }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                                            <Typography variant="subtitle1" sx={{ fontWeight: 900, color: '#111827' }}>
                                                {tableName}
                                            </Typography>
                                            <Chip
                                                icon={timerIcon}
                                                label={`${minsElapsed}m`}
                                                size="small"
                                                sx={{
                                                    bgcolor: timerColor + '15',
                                                    color: timerColor,
                                                    fontWeight: 800,
                                                    borderColor: 'transparent'
                                                }}
                                            />
                                        </Box>
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            <Chip
                                                label={hasNonVeg ? "🚨 CONTAINS NON-VEG" : "🟢 PURE VEG PREPARATION"}
                                                size="small"
                                                sx={{
                                                    alignSelf: 'flex-start',
                                                    bgcolor: hasNonVeg ? '#fef2f2' : '#f0fdf4',
                                                    color: hasNonVeg ? '#ef4444' : '#10b981',
                                                    fontWeight: 800,
                                                    fontSize: '0.65rem',
                                                    border: `1px solid ${hasNonVeg ? '#fee2e2' : '#dcfce7'}`,
                                                    borderRadius: 1,
                                                    height: 22
                                                }}
                                            />
                                            <Typography variant="caption" sx={{ color: hasNonVeg ? '#dc2626' : '#16a34a', fontWeight: 800, fontSize: '0.65rem' }}>
                                                FSSAI: {hasNonVeg ? "Cook in NON-VEG section only!" : "Cook in PURE VEG section only!"}
                                            </Typography>
                                        </Box>
                                    </Box>

                                    {/* Card Content (Items) */}
                                    <CardContent sx={{ p: 2, flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        <Box sx={{ display: 'flex', justify: 'space-between', color: '#6b7280', fontSize: '0.75rem', fontWeight: 600, mb: 0.5 }}>
                                            <span>ID: {order.id}</span>
                                            <span>Waiter: {order.waiterName}</span>
                                        </Box>
                                        
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flexGrow: 1 }}>
                                            {filteredItems.map(item => {
                                                const isCrossed = crossedItems[`${order.id}-${item.id}`];
                                                const menuItem = menuItems.find(m => m.id === item.id);
                                                const itemName = currentLanguage === 'en' ? item.name : (currentLanguage === 'mr' ? (menuItem?.nameMr || item.name) : (menuItem?.nameHi || item.name));
                                                
                                                return (
                                                    <Box 
                                                        key={item.id} 
                                                        onClick={() => toggleCrossItem(order.id, item.id)}
                                                        sx={{ 
                                                            display: 'flex', 
                                                            gap: 1.5,
                                                            cursor: 'pointer',
                                                            opacity: isCrossed ? 0.4 : 1,
                                                            '&:hover': { opacity: 0.85 }
                                                        }}
                                                    >
                                                        <Box sx={{ 
                                                            bgcolor: '#111827', 
                                                            color: '#ffffff', 
                                                            fontWeight: 800, 
                                                            width: 24, 
                                                            height: 24, 
                                                            borderRadius: 1, 
                                                            display: 'flex', 
                                                            alignItems: 'center', 
                                                            justifyContent: 'center',
                                                            fontSize: '0.85rem'
                                                        }}>
                                                            {item.quantity}
                                                        </Box>
                                                        <Box sx={{ flexGrow: 1 }}>
                                                            <Typography variant="body2" sx={{ 
                                                                fontWeight: 700, 
                                                                color: '#111827',
                                                                textDecoration: isCrossed ? 'line-through' : 'none'
                                                            }}>
                                                                {itemName}
                                                            </Typography>
                                                            {item.remarks && (
                                                                <Typography variant="caption" display="block" sx={{ color: '#ef4444', fontWeight: 700 }}>
                                                                    * {item.remarks}
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                    </Box>
                                                );
                                            })}
                                        </Box>
                                    </CardContent>

                                    {/* Action Footer */}
                                    <Box sx={{ p: 2, borderTop: '1px solid #e5e7eb' }}>
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            color={order.status === 'preparing' ? 'warning' : 'success'}
                                            onClick={() => advanceOrderStatus(order.id)}
                                            sx={{
                                                textTransform: 'none',
                                                fontWeight: 800,
                                                py: 1,
                                                borderRadius: 2,
                                                bgcolor: order.status === 'preparing' ? '#f59e0b' : '#10b981',
                                                '&:hover': {
                                                    bgcolor: order.status === 'preparing' ? '#d97706' : '#059669'
                                                }
                                            }}
                                        >
                                            {buttonText}
                                        </Button>
                                    </Box>
                                </Card>
                            </Grid>
                        );
                    })}

                    {/* Empty State */}
                    {renderedCount === 0 && (
                        <Grid item xs={12}>
                            <Box sx={{ 
                                display: 'flex', 
                                flexDirection: 'column', 
                                alignItems: 'center', 
                                justifyContent: 'center',
                                py: 12,
                                bgcolor: '#ffffff',
                                border: '1px solid #e5e7eb',
                                borderRadius: 3,
                                color: '#9ca3af',
                                gap: 1.5
                            }}>
                                <CheckCircleIcon sx={{ color: '#10b981', fontSize: 60 }} />
                                <Typography variant="h6" sx={{ fontWeight: 800, color: '#374151' }}>
                                    {t('kds-clear')}
                                </Typography>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </Box>
        </Box>
    );
}
